import {useState} from 'react';
import {CopyOutlined} from '@ant-design/icons';
import {message, Pagination, Typography} from 'antd';
import UserLink from 'components/userLink/UserLink';
import styles from '../kycDashboard.module.scss';
import {firstHttpUrl, isLikelyHttpUrl} from '../kycComplyCubeUtils';

const {Text} = Typography;

export function LivePhotoThumb({src}) {
  const [broken, setBroken] = useState(false);
  const url = typeof src === 'string' ? src : null;
  if (!url || !isLikelyHttpUrl(url) || broken) {
    return (
      <Text type="secondary" className={styles.thumbTableMuted}>
        —
      </Text>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className={styles.thumbTable}
      onError={() => setBroken(true)}
    />
  );
}

export function ClientIdLink({id, onOpen}) {
  if (!id) return '-';
  return (
    <button
      type="button"
      className={styles.clientIdLink}
      onClick={e => {
        e.stopPropagation();
        onOpen(id);
      }}>
      {id}
    </button>
  );
}

const CLIENT_ID_HEAD = 6;
const CLIENT_ID_TAIL = 4;

/** Shortened ID + copy; copy click stops propagation for row-level handlers. */
export function ComplyCubeTruncatedClientId({
  id,
  copiedMessage = 'Client ID copied',
  copyAriaLabel = 'Copy client ID',
}) {
  if (!id) return '—';
  const full = String(id);
  const display =
    full.length > CLIENT_ID_HEAD + CLIENT_ID_TAIL + 2
      ? `${full.slice(0, CLIENT_ID_HEAD)}…${full.slice(-CLIENT_ID_TAIL)}`
      : full;

  const handleCopy = e => {
    e.stopPropagation();
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(full);
      message.success(copiedMessage);
    } else {
      message.error('Clipboard not available');
    }
  };

  return (
    <span className={styles.clientIdCellWrap}>
      <span className={styles.clientIdTruncatedText} title={full}>
        {display}
      </span>
      <button
        type="button"
        className={styles.clientIdCopyBtn}
        aria-label={copyAriaLabel}
        onClick={handleCopy}>
        <CopyOutlined />
      </button>
    </span>
  );
}

/**
 * Stacked display name + email; clicking the name opens the admin user overview in a new tab.
 * Stops propagation so parent row handlers (e.g. open ComplyCube client) do not run.
 */
export function ComplyCubeUserCell({user}) {
  const id = user?.id;
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    (user?.firstName || '').trim() ||
    '—';
  const email = (user?.email || '').trim() || '—';

  return (
    <div
      className={styles.complyCubeUserCell}
      role="presentation"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}>
      {id ? (
        <UserLink
          userId={id}
          tab="overview"
          className={styles.complyCubeUserNameBtn}>
          {name}
        </UserLink>
      ) : (
        <span className={styles.complyCubeUserNameMuted}>{name}</span>
      )}
      <div className={styles.complyCubeUserEmail}>{email}</div>
    </div>
  );
}

/**
 * When API omits total, derive a minimal total so Ant Pagination next/prev behaves.
 */
function resolvePaginationTotal(page, limit, rowCount, totalFromApi) {
  if (totalFromApi != null) return totalFromApi;
  if (rowCount === 0 && page <= 1) return 0;
  if (rowCount < limit) return (page - 1) * limit + rowCount;
  return page * limit + 1;
}

export function ComplyCubeTablePagination({
  page,
  limit,
  total: totalFromApi,
  rowCount,
  loading,
  onPageChange,
  itemName = 'items',
}) {
  const total = resolvePaginationTotal(page, limit, rowCount, totalFromApi);
  if (total <= 0 && page <= 1) return null;

  const handleChange = (nextPage, nextSize) => {
    if (nextSize !== limit) {
      onPageChange(1, nextSize);
    } else {
      onPageChange(nextPage, nextSize);
    }
  };

  return (
    <Pagination
      className={styles.viewerAntPagination}
      current={page}
      pageSize={limit}
      total={total}
      disabled={loading}
      onChange={handleChange}
      showSizeChanger
      showQuickJumper
      pageSizeOptions={['10', '20', '50']}
      showTotal={
        totalFromApi != null
          ? (t, range) => `${range[0]}-${range[1]} of ${t} ${itemName}`
          : undefined
      }
    />
  );
}

export {firstHttpUrl};
